import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Badge,
  BottomNavigation,
  BottomNavigationAction,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import TimelineIcon from '@mui/icons-material/Timeline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ShareIcon from '@mui/icons-material/Share';
import GroupsIcon from '@mui/icons-material/Groups';
import SettingsIcon from '@mui/icons-material/Settings';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import AddCardIcon from '@mui/icons-material/AddCard';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import LogoutIcon from '@mui/icons-material/Logout';
import HomeIcon from '@mui/icons-material/Home';
import CloseIcon from '@mui/icons-material/Close';
import EastIcon from '@mui/icons-material/East';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useColorMode } from '../../contexts/ColorModeContext';
import { fetchDriverWallet } from '../../services/driverWorkspaceService';
import { getSocket } from '../../services/socketService';
import api from '../../services/api';
import { WalletSummary } from '../../types';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnread';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';

interface RideNotification {
  id: string;
  from: string;
  to: string;
  priority: 'high' | 'normal' | 'low';
  vehicleType?: string;
  dutyType?: string;
  createdAt: string;
  seen: boolean;
}

const NOTIF_STORAGE_KEY = 'ride_notifications_v1';
const NOTIF_MAX = 50;

const loadNotifs = (): RideNotification[] => {
  try {
    return JSON.parse(localStorage.getItem(NOTIF_STORAGE_KEY) || '[]');
  } catch {
    return [];
  }
};

const saveNotifs = (notifs: RideNotification[]) => {
  try {
    localStorage.setItem(NOTIF_STORAGE_KEY, JSON.stringify(notifs));
  } catch { /* ignore */ }
};

const timeAgo = (iso: string): string => {
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
};

const sidebarItems = [
  { label: 'Dashboard', path: '/app', icon: <DashboardIcon /> },
  { label: 'Rides', path: '/app/rides', icon: <DirectionsCarIcon /> },
  { label: 'Wallet', path: '/wallet', icon: <AccountBalanceWalletIcon /> },
  { label: 'Activity', path: '/activity', icon: <TimelineIcon /> },
  { label: 'Earnings', path: '/earnings', icon: <TrendingUpIcon /> },
  { label: 'Referrals', path: '/referrals', icon: <ShareIcon /> },
  { label: 'Fleet', path: '/fleet', icon: <GroupsIcon /> },
  { label: 'Settings', path: '/settings', icon: <SettingsIcon /> },
  { label: 'Support', path: '/support', icon: <SupportAgentIcon /> },
];

const adminSidebarItems = [
  { label: 'Top-ups', path: '/admin/topups', icon: <AddCardIcon /> },
];

const mobileItems = [
  { label: 'Rides', path: '/app/rides', icon: <DirectionsCarIcon /> },
  { label: 'Home', path: '/app', icon: <DashboardIcon /> },
  { label: 'Wallet', path: '/wallet', icon: <AccountBalanceWalletIcon /> },
  { label: 'Activity', path: '/activity', icon: <TimelineIcon /> },
  { label: 'Settings', path: '/settings', icon: <SettingsIcon /> },
];

const pageTitles: Record<string, string> = {
  '/app': 'Driver workspace',
  '/rides': 'Live Rides',
  '/app/rides': 'Live Rides',
  '/wallet': 'Wallet',
  '/activity': 'Activity',
  '/earnings': 'Earnings',
  '/referrals': 'Referrals',
  '/fleet': 'Fleet',
  '/settings': 'Settings',
  '/admin/topups': 'Top-up Packs',
  '/support': 'Support',
  '/onboarding': 'Onboarding',
};


const DriverAppLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, logout } = useAuth();
  const { mode, toggleMode } = useColorMode();
  const location = useLocation();
  const navigate = useNavigate();
  const [wallet, setWallet] = useState<WalletSummary | null>(null);
  const [isOnline, setIsOnline] = useState(false);
  const [profileMenu, setProfileMenu] = useState<null | HTMLElement>(null);
  const [notifOpen, setNotifOpen] = useState(false);
  const [rideNotifs, setRideNotifs] = useState<RideNotification[]>(loadNotifs);
  const [resendStatus, setResendStatus] = useState<'idle' | 'sending' | 'sent'>('idle');
  const notifListRef = useRef<HTMLDivElement>(null);

  const unreadCount = rideNotifs.filter((n) => !n.seen).length;
  const navItems = useMemo(
    () => (user?.role === 'admin' ? [...sidebarItems, ...adminSidebarItems] : sidebarItems),
    [user?.role]
  );

  // Persist notifs whenever they change
  useEffect(() => { saveNotifs(rideNotifs); }, [rideNotifs]);

  // Listen for new rides via socket
  useEffect(() => {
    const socket = getSocket();
    const handleNewRide = (data: any) => {
      const notif: RideNotification = {
        id: String(data._id || data.id || ''),
        from: data.aiAnalysis?.from || data.from || '',
        to: data.aiAnalysis?.to || data.to || '',
        priority: data.priority || 'normal',
        vehicleType: data.aiAnalysis?.vehicleType || undefined,
        dutyType: data.aiAnalysis?.dutyType || undefined,
        createdAt: data.createdAt || new Date().toISOString(),
        seen: false,
      };
      if (!notif.id) return;
      setRideNotifs((prev) => {
        const deduped = prev.filter((n) => n.id !== notif.id);
        return [notif, ...deduped].slice(0, NOTIF_MAX);
      });
    };
    socket.on('newRide', handleNewRide);
    return () => { socket.off('newRide', handleNewRide); };
  }, []);

  // Mark all seen when user is actively on rides page
  useEffect(() => {
    if (location.pathname === '/app/rides') {
      setRideNotifs((prev) => {
        if (prev.every((n) => n.seen)) return prev;
        return prev.map((n) => ({ ...n, seen: true }));
      });
    }
  }, [location.pathname]);

  useEffect(() => {
    fetchDriverWallet().then(setWallet).catch(() => setWallet(null));
    api.get('/api/users/profile')
      .then((response) => setIsOnline(Boolean(response.data.isOnline)))
      .catch(() => {});
  }, [location.pathname]);

  const currentPath = useMemo(() => {
    const exact = [...navItems, { path: '/onboarding', label: 'Onboarding' }, { path: '/rides', label: 'Rides' }]
      .find((item) => location.pathname === item.path);
    return exact?.path || '/app';
  }, [location.pathname, navItems]);

  const creditsLabel = wallet?.entitlements.monthlyLeadLimit === null
    ? 'Unlimited'
    : `${wallet?.entitlements.remaining ?? 0} left`;

  const handleOnline = (next: boolean) => {
    setIsOnline(next);
    api.patch('/api/users/online', { isOnline: next }).catch(() => setIsOnline(!next));
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const openNotifications = useCallback(() => {
    setNotifOpen(true);
    setRideNotifs((prev) => prev.map((n) => ({ ...n, seen: true })));
  }, []);

  const handleResendVerification = () => {
    setResendStatus('sending');
    api.post('/api/auth/resend-verification')
      .then(() => setResendStatus('sent'))
      .catch(() => setResendStatus('idle'));
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f7fb', display: 'flex' }}>
      <Box
        component="aside"
        sx={{
          display: { xs: 'none', md: 'flex' },
          width: 260,
          flexShrink: 0,
          bgcolor: '#061b31',
          color: 'white',
          flexDirection: 'column',
          position: 'sticky',
          top: 0,
          height: '100vh',
          borderRight: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        <Box component={RouterLink} to="/" sx={{ p: 2.5, textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
          <Box component="img" src="/assets/ride-saathi-main-logo.png" alt="RideSaathi" sx={{ height: 38, width: 'auto', maxWidth: '100%', display: 'block', objectFit: 'contain', filter: 'brightness(0) invert(1)' }} />
        </Box>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />
        <Stack spacing={0.5} sx={{ p: 1.5, flex: 1 }}>
          {navItems.map((item) => {
            const active = currentPath === item.path;
            return (
              <Button
                key={item.path}
                component={RouterLink}
                to={item.path}
                startIcon={item.icon}
                fullWidth
                sx={{
                  justifyContent: 'flex-start',
                  color: active ? '#07182d' : 'rgba(255,255,255,0.82)',
                  bgcolor: active ? '#ffc928' : 'transparent',
                  borderRadius: 1,
                  py: 1.1,
                  '&:hover': {
                    bgcolor: active ? '#ffc928' : 'rgba(255,255,255,0.07)',
                    color: active ? '#07182d' : 'white',
                  },
                }}
              >
                {item.label}
              </Button>
            );
          })}
        </Stack>
        <Box sx={{ p: 2 }}>
          <Button
            component={RouterLink}
            to="/"
            startIcon={<HomeIcon />}
            fullWidth
            sx={{
              justifyContent: 'flex-start',
              color: 'rgba(255,255,255,0.82)',
              mb: 1,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.07)', color: 'white' },
            }}
          >
            Public home
          </Button>
          <Paper sx={{ p: 1.5, bgcolor: 'rgba(255,255,255,0.06)', color: 'white', border: '1px solid rgba(255,255,255,0.08)' }}>
            <Typography variant="caption" color="rgba(255,255,255,0.62)">Credits</Typography>
            <Typography variant="h6" fontWeight={900}>{creditsLabel}</Typography>
            {wallet?.lowCredits && (
              <Button component={RouterLink} to="/wallet" size="small" color="secondary" sx={{ mt: 0.5, px: 0 }}>
                Add credits
              </Button>
            )}
          </Paper>
        </Box>
      </Box>

      <Box sx={{ flex: 1, minWidth: 0, pb: { xs: 8, md: 0 } }}>
        <Box
          component="header"
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 20,
            bgcolor: 'rgba(245,247,251,0.92)',
            backdropFilter: 'blur(18px)',
            borderBottom: '1px solid rgba(7,24,45,0.08)',
            px: { xs: 2, md: 3 },
            py: 1.5,
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" gap={1.5}>
            <Box minWidth={0}>
              <Typography variant="h6" fontWeight={900} noWrap sx={{ fontSize: { xs: 17, sm: 20 } }}>
                {pageTitles[currentPath] || 'Driver workspace'}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                {user?.name ? `Signed in as ${user.name}` : 'Driver account'}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Tooltip title="Public home">
                <IconButton component={RouterLink} to="/" sx={{ display: { xs: 'none', sm: 'inline-flex' } }}>
                  <HomeIcon />
                </IconButton>
              </Tooltip>
              <Chip
                label={creditsLabel}
                size="small"
                color={wallet?.lowCredits ? 'warning' : 'default'}
                sx={{ display: { xs: 'none', sm: 'inline-flex' }, bgcolor: wallet?.lowCredits ? undefined : 'white' }}
              />
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ display: { xs: 'none', sm: 'flex' } }}>
                <Typography variant="caption" color="text.secondary">{isOnline ? 'Online' : 'Offline'}</Typography>
                <Switch checked={isOnline} onChange={(event) => handleOnline(event.target.checked)} color="success" size="small" />
              </Stack>
              <Tooltip title={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
                <IconButton onClick={toggleMode}>
                  {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
                </IconButton>
              </Tooltip>
              <Tooltip title="New ride notifications">
                <IconButton onClick={openNotifications}>
                  <Badge badgeContent={unreadCount || undefined} color="error" max={9}>
                    {unreadCount > 0
                      ? <NotificationsActiveIcon sx={{ color: '#ffc928' }} />
                      : <NotificationsNoneIcon />}
                  </Badge>
                </IconButton>
              </Tooltip>
              <IconButton onClick={(event) => setProfileMenu(event.currentTarget)}>
                <Badge color="success" variant={isOnline ? 'dot' : 'standard'}>
                  <Avatar sx={{ width: 34, height: 34, bgcolor: '#08233f', fontSize: 14 }}>
                    {(user?.name || user?.email || 'D').slice(0, 1).toUpperCase()}
                  </Avatar>
                </Badge>
              </IconButton>
            </Stack>
          </Box>
        </Box>

        {/* Email verification banner */}
        {user && user.emailVerified === false && (
          <Alert
            severity="warning"
            icon={<MarkEmailUnreadIcon />}
            action={
              <Button
                color="inherit"
                size="small"
                sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}
                disabled={resendStatus === 'sending' || resendStatus === 'sent'}
                onClick={handleResendVerification}
              >
                {resendStatus === 'sent' ? 'Email sent ✓' : resendStatus === 'sending' ? 'Sending…' : 'Resend email'}
              </Button>
            }
            sx={{ borderRadius: 0, borderBottom: '1px solid rgba(7,24,45,0.08)' }}
          >
            Please verify your email <strong>{user.email}</strong> to perform actions on this platform.
          </Alert>
        )}

        {/* Subscription renewal / expired banner */}
        {wallet && (
          wallet.renewalDaysLeft !== null && wallet.renewalDaysLeft <= 3 && wallet.renewalDaysLeft > 0 ? (
            <Alert
              severity="warning"
              icon={<WarningAmberIcon />}
              action={
                <Button component={RouterLink} to="/wallet" color="inherit" size="small" sx={{ fontWeight: 700 }}>
                  Renew
                </Button>
              }
              sx={{ borderRadius: 0, borderBottom: '1px solid rgba(7,24,45,0.08)' }}
            >
              Your plan expires in {wallet.renewalDaysLeft} day{wallet.renewalDaysLeft !== 1 ? 's' : ''}. Renew now to keep your rides flowing.
            </Alert>
          ) : wallet.subscription === null && wallet.entitlements.remaining === 0 ? (
            <Alert
              severity="error"
              action={
                <Button component={RouterLink} to="/wallet" color="inherit" size="small" sx={{ fontWeight: 700 }}>
                  Top up
                </Button>
              }
              sx={{ borderRadius: 0, borderBottom: '1px solid rgba(7,24,45,0.08)' }}
            >
              You've used all available credits. Add more credits to unlock more rides.
            </Alert>
          ) : null
        )}
        <Box component="main" sx={{ px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 } }}>
          {children}
        </Box>
      </Box>

      <Paper
        elevation={8}
        sx={{
          display: { xs: 'block', md: 'none' },
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 30,
          borderTop: '1px solid rgba(7,24,45,0.08)',
        }}
      >
        <BottomNavigation
          value={mobileItems.some((item) => item.path === currentPath) ? currentPath : '/app'}
          onChange={(_, value) => navigate(value)}
          showLabels
        >
          {mobileItems.map((item) => (
            <BottomNavigationAction key={item.path} label={item.label} value={item.path} icon={item.icon} />
          ))}
        </BottomNavigation>
      </Paper>

      <Menu
        anchorEl={profileMenu}
        open={Boolean(profileMenu)}
        onClose={() => setProfileMenu(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem component={RouterLink} to="/settings" onClick={() => setProfileMenu(null)}>
          <SettingsIcon fontSize="small" sx={{ mr: 1 }} /> Settings
        </MenuItem>
        <MenuItem component={RouterLink} to="/" onClick={() => setProfileMenu(null)}>
          <HomeIcon fontSize="small" sx={{ mr: 1 }} /> Public home
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <LogoutIcon fontSize="small" sx={{ mr: 1 }} /> Logout
        </MenuItem>
      </Menu>

      {/* Ride notifications drawer */}
      <Drawer
        anchor="right"
        open={notifOpen}
        onClose={() => setNotifOpen(false)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 380 }, display: 'flex', flexDirection: 'column' } }}
      >
        {/* Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          px={2.5}
          py={2}
          sx={{ borderBottom: '1px solid rgba(7,24,45,0.08)', flexShrink: 0 }}
        >
          <Box>
            <Typography variant="h6" fontWeight={900}>New rides</Typography>
            <Typography variant="caption" color="text.secondary">
              {rideNotifs.length === 0
                ? 'No new rides yet'
                : `${rideNotifs.length} ride${rideNotifs.length !== 1 ? 's' : ''} received`}
            </Typography>
          </Box>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            {rideNotifs.length > 0 && (
              <Button
                size="small"
                variant="text"
                onClick={() => setRideNotifs([])}
                sx={{ fontSize: 11, color: 'text.secondary' }}
              >
                Clear all
              </Button>
            )}
            <IconButton onClick={() => setNotifOpen(false)}><CloseIcon /></IconButton>
          </Stack>
        </Box>

        {/* Notification list */}
        <Box ref={notifListRef} sx={{ flex: 1, overflowY: 'auto' }}>
          {rideNotifs.length === 0 ? (
            <Box p={4} textAlign="center">
              <NotificationsNoneIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body2" color="text.secondary" fontWeight={600}>No new rides yet</Typography>
              <Typography variant="caption" color="text.secondary">
                New ride requests will appear here in real time.
              </Typography>
            </Box>
          ) : (
            <Stack divider={<Divider />}>
              {rideNotifs.map((notif) => (
                <Box
                  key={notif.id}
                  px={2.5}
                  py={1.75}
                  sx={{
                    cursor: 'pointer',
                    bgcolor: notif.seen ? 'transparent' : 'rgba(255,201,40,0.05)',
                    borderLeft: notif.seen ? '3px solid transparent' : '3px solid #ffc928',
                    transition: 'background 0.15s',
                    '&:hover': { bgcolor: 'rgba(7,24,45,0.04)' },
                  }}
                  onClick={() => {
                    setNotifOpen(false);
                    navigate(`/app/rides?openRide=${notif.id}`);
                  }}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                    <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
                      <Typography variant="body2" fontWeight={700} sx={{ fontSize: 14 }}>
                        {notif.from || 'Pickup'}
                      </Typography>
                      <EastIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                      <Typography variant="body2" fontWeight={700} sx={{ fontSize: 14 }}>
                        {notif.to || 'Drop'}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, ml: 1 }}>
                      {timeAgo(notif.createdAt)}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    <Chip
                      label={notif.priority}
                      size="small"
                      variant="outlined"
                      sx={{
                        fontSize: 11,
                        height: 20,
                        textTransform: 'capitalize',
                        borderColor: notif.priority === 'high' ? 'rgba(198,40,40,0.4)' : 'rgba(0,0,0,0.18)',
                        color: notif.priority === 'high' ? '#c62828' : 'text.secondary',
                      }}
                    />
                    {notif.vehicleType && (
                      <Chip
                        label={notif.vehicleType}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: 11, height: 20, textTransform: 'capitalize', borderColor: 'rgba(0,0,0,0.18)', color: 'text.secondary' }}
                      />
                    )}
                    {notif.dutyType && (
                      <Chip
                        label={notif.dutyType}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: 11, height: 20, borderColor: 'rgba(0,0,0,0.18)', color: 'text.secondary' }}
                      />
                    )}
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}
        </Box>

        {/* Footer */}
        <Box px={2} py={1.5} sx={{ borderTop: '1px solid rgba(7,24,45,0.08)', flexShrink: 0 }}>
          <Button
            fullWidth
            variant="contained"
            component={RouterLink}
            to="/app/rides"
            onClick={() => setNotifOpen(false)}
            sx={{ bgcolor: '#08233f', color: 'white', fontWeight: 700, textTransform: 'none', '&:hover': { bgcolor: '#0b3156' } }}
          >
            View all live rides
          </Button>
        </Box>
      </Drawer>
    </Box>
  );
};

export default DriverAppLayout;
